import React, { useEffect, useState } from 'react';
import { GrowthBookProvider as BaseGrowthBookProvider } from '@growthbook/growthbook-react';
import { GrowthBook } from '@growthbook/growthbook-react';
import configJSON from 'config.json';
import { ENVIRONMENT_PRODUCTION, getEnvironment, isEndToEndTest } from 'utils/environment';
import { trackGrowthbookEvent } from 'utils/google-tag-manager';
import { getOrCreateDeviceId } from 'utils/device-id';
import Cookies from 'js-cookie';
import { AREA_PAGE_FOURTH_MAP_CARD_COOKIE_NAME, MAP_SINGLE_LISTING_PREVIEW_COOKIE_NAME } from 'constants/cookies';
import { AREA_PAGE_FOURTH_MAP_CARD_FLAG, MAP_SINGLE_LISTING_PREVIEW_FLAG } from 'constants/feature-flags';
import { User } from 'contexts/user';

interface GrowthBookProviderProps {
  children: React.ReactNode;
  user?: User;
  deviceId?: string;
}

// This is a wrapper around the GrowthBookProvider component that initializes the GrowthBook instance and sets the attributes
export const GrowthBookProvider: React.FC<GrowthBookProviderProps> = ({ children, user, deviceId }) => {
  const [growthbook] = useState(() => new GrowthBook({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: configJSON.growthbook.clientKey,
    enableDevMode: getEnvironment() !== ENVIRONMENT_PRODUCTION,
    subscribeToChanges: true,
    trackingCallback: (experiment, result) => {
      trackGrowthbookEvent(experiment.key, result.variationId);
    },
  }));

  useEffect(() => {
    if (isEndToEndTest) {
      return;
    }

    const initialize = async () => {
      try {
        const currentDeviceId = deviceId || getOrCreateDeviceId();
        growthbook.setAttributes({ id: currentDeviceId, anonymous_id: currentDeviceId, loggedIn: user?.id });
        await growthbook.init();

        const showMapCardExperimentView = growthbook.getFeatureValue(AREA_PAGE_FOURTH_MAP_CARD_FLAG, 0) == 1;
        Cookies.set(AREA_PAGE_FOURTH_MAP_CARD_COOKIE_NAME, showMapCardExperimentView.toString());

        // Set cookie for map single listing preview experiment
        const showMapSingleListingPreview = growthbook.getFeatureValue(MAP_SINGLE_LISTING_PREVIEW_FLAG, 0) == 1;
        Cookies.set(MAP_SINGLE_LISTING_PREVIEW_COOKIE_NAME, showMapSingleListingPreview.toString());
      } catch (error) {
        console.error('GrowthBook initialization failed:', error);
      }
    };

    initialize();
  }, [user, deviceId, growthbook]);

  return (
    <BaseGrowthBookProvider growthbook={growthbook}>
      {children}
    </BaseGrowthBookProvider>
  );
};